import { useMemo } from "react";
import { InternalConnector, WalletProvider } from "../Models/WalletProvider";
import { removeDuplicatesWithKey, sortRecentConnectors } from "../components/WalletModal/utils";
import { featuredWalletsIds } from "@/context/evmConnectorsContext";

type UseConnectorsParams = {
    searchValue?: string;
    recentConnectors: { providerName?: string; connectorName?: string }[];
    featuredProviders: WalletProvider[];
    filteredProviders: WalletProvider[];
};

export function useConnectors({
    featuredProviders,
    filteredProviders,
    searchValue,
    recentConnectors,
}: UseConnectorsParams) {

    const featuredConnectors = useMemo(() =>
        featuredProviders
            .filter(g => g.availableWalletsForConnect && g.availableWalletsForConnect?.length > 0)
            .map((provider) =>
                provider.availableWalletsForConnect
                    ?.filter(v => searchValue ? v.name.toLowerCase().includes(searchValue.toLowerCase()) : true)
                    .map((connector) => ({ ...connector, providerName: provider.name }))
            )
            .flat() as InternalConnector[],
        [featuredProviders, searchValue]
    );

    const hiddenConnectors = useMemo(() =>
        featuredProviders
            .filter(g => g.availableHiddenWalletsForConnect && g.availableHiddenWalletsForConnect?.length > 0)
            .map((provider) =>
                provider.availableHiddenWalletsForConnect
                    ?.filter(v =>
                        (searchValue ? v.name.toLowerCase().includes(searchValue.toLowerCase()) : true) &&
                        !featuredWalletsIds.includes(v.id.toLowerCase())
                    )
                    .map((connector) => ({ ...connector, providerName: provider.name, isHidden: true }))
            )
            .flat() as InternalConnector[],
        [featuredProviders, searchValue]
    );


    const initialConnectors: InternalConnector[] = useMemo(() => {
        return removeDuplicatesWithKey(
            ([...featuredConnectors, ...hiddenConnectors] as InternalConnector[])
                .sort((a, b) => sortRecentConnectors(a, b, recentConnectors)),
            'name'
        );
    }, [featuredConnectors, hiddenConnectors, searchValue?.length, recentConnectors]);

    return {
        featuredConnectors,
        hiddenConnectors,
        initialConnectors,
        featuredProviders,
        filteredProviders,
    };
}

